import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import React, { useEffect, useState } from 'react';
import { MdLocationPin } from 'react-icons/md';
import { Spinner } from 'react-bootstrap';
import { useMyLocationContext } from '../../context';
import { useToast } from '../../context';
import { SavedLocationSelection } from '../../services/helper';

interface BrowserLocationProps {
  isModal?: boolean;
  onLocationSet?: (location: SavedLocationSelection) => void;
}

const geolocationErrorMessage = (code: number) => {
  switch (code) {
    case GeolocationPositionError.PERMISSION_DENIED:
      return 'Location permission was denied. Allow location access in your browser settings and try again.';
    case GeolocationPositionError.POSITION_UNAVAILABLE:
      return 'Your current location could not be determined.';
    case GeolocationPositionError.TIMEOUT:
      return 'Timed out while fetching your location. Please try again.';
    default:
      return 'Unable to fetch your current location.';
  }
};

const savedLocationFromGeocodeResult = (result: google.maps.GeocoderResult): SavedLocationSelection => {
  const components = [...result.address_components];
  const zipIndex = components.findIndex((ac) => ac.types.includes('postal_code'));
  const zip = zipIndex >= 0 ? components.splice(zipIndex, 1)[0] : undefined;
  const shortLabel = result.formatted_address;
  const { lat, lng } = result.geometry.location.toJSON();
  return {
    label: zip ? String(zip.short_name) : shortLabel,
    location: { lat, lng }
  };
};

const savedLocationFromCoords = (latitude: number, longitude: number): SavedLocationSelection => ({
  label: 'Current location',
  location: { lat: latitude, lng: longitude }
});

const BrowserLocationInner: React.FC<BrowserLocationProps> = ({ isModal = false, onLocationSet }) => {
  const { myLocation, saveMyLocation } = useMyLocationContext();
  const { addToast } = useToast();
  const GeocodingLibrary = useMapsLibrary('geocoding');
  const [geocodingService, setGeocodingService] = useState<google.maps.Geocoder>();
  const [loading, setLoading] = useState(false);
  const enabled = typeof navigator !== 'undefined' && 'geolocation' in navigator;

  useEffect(() => {
    if (!GeocodingLibrary) return;
    setGeocodingService(new GeocodingLibrary.Geocoder());
  }, [GeocodingLibrary]);

  const persistLocation = (saved: SavedLocationSelection) => {
    saveMyLocation(JSON.stringify(saved), !myLocation);
    onLocationSet?.(saved);
  };

  const reverseGeocodeAndSave = (latitude: number, longitude: number) => {
    if (!geocodingService) {
      persistLocation(savedLocationFromCoords(latitude, longitude));
      setLoading(false);
      return;
    }

    geocodingService.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
      setLoading(false);
      if (status === google.maps.GeocoderStatus.OK && results?.[0]) {
        persistLocation(savedLocationFromGeocodeResult(results[0]));
        return;
      }
      persistLocation(savedLocationFromCoords(latitude, longitude));
    });
  };

  const getLocation = () => {
    if (loading) return;

    if (!enabled) {
      addToast('Geolocation is not supported by this browser.', 'error');
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        reverseGeocodeAndSave(latitude, longitude);
      },
      (error) => {
        setLoading(false);
        addToast(geolocationErrorMessage(error.code), 'error');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const content = (
    <button
      type="button"
      className={`header-search-link link-header mt-3 border-0 bg-transparent p-0 ${
        isModal ? 'text-blue' : ''
      }`}
      onClick={getLocation}
      disabled={loading}
    >
      {loading ? (
        <>
          <Spinner
            animation="border"
            size="sm"
            className="me-2"
            role="status"
          />
          Finding your location...
        </>
      ) : (
        <>
          <MdLocationPin
            size={16}
            className="me-2"
          />
          Use current Location
        </>
      )}
    </button>
  );

  return enabled ? content : <s>{content}</s>;
};

const BrowserLocationComponent: React.FC<BrowserLocationProps> = (props) => {
  const googleApiKey = process.env.REACT_APP_GOOGLE_API_KEY || '';

  return (
    <APIProvider apiKey={googleApiKey}>
      <BrowserLocationInner {...props} />
    </APIProvider>
  );
};

export default BrowserLocationComponent;
